import { useRecordContext } from 'react-admin';

/**
 * Formats a duration in milliseconds to a human-readable string.
 * - < 1s: "0.Xs" (e.g., "0.8s")
 * - 1-60s: "Xs" (e.g., "45s")
 * - 1-60m: "Xm Ys" (e.g., "2m 15s")
 * - >= 60m: "Xh Ym" (e.g., "1h 23m")
 *
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string
 */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 0) return '—';

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (ms < 1000) {
    // Sub-second: show decimal
    return `${(ms / 1000).toFixed(1)}s`;
  }

  if (seconds < 60) {
    return `${seconds}s`;
  }

  if (minutes < 60) {
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  // Hours
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

/**
 * Formats a date/time string according to specification:
 * - Same day: HH:mm:ss (24-hour format)
 * - Different day: M/D HH:mm:ss (no zero-padding)
 * - Empty/null: em-dash (—)
 *
 * @param dateString - ISO date string or Date-compatible string
 * @returns Formatted date/time string or em-dash if empty
 */
export const formatDateTime = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '—';

  const date = new Date(dateString);

  // Check if date is valid
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  // Format time as HH:mm:ss (24-hour, zero-padded)
  const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

  if (isToday) {
    return timeStr;
  }

  // Format date as M/D (no zero-padding)
  const month = date.getMonth() + 1; // 0-indexed, so add 1
  const day = date.getDate();

  return `${month}/${day} ${timeStr}`;
};

interface DateTimeFieldProps {
  source: string;
}

/**
 * React-Admin field component for displaying formatted date/time.
 * Shows time only for today's dates, full date/time for others.
 */
export const DateTimeField = ({ source }: DateTimeFieldProps) => {
  const record = useRecordContext();

  if (!record) return null;

  const dateValue = record[source];
  const formattedDate = formatDateTime(dateValue);

  return <span>{formattedDate}</span>;
};

DateTimeField.displayName = 'DateTime';

/**
 * Formats a date string to display only the date (no time).
 *
 * @param dateString - ISO date string or Date-compatible string
 * @returns Formatted date string in ISO format (e.g., "2026-03-15") or em-dash if empty
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';

  // Extract date portion from ISO string (YYYY-MM-DD)
  const datePart = dateString.split('T')[0];
  return datePart ?? '—';
};

interface DateFieldProps {
  source: string;
}

/**
 * React-Admin field component for displaying formatted date (no time).
 * Shows em-dash for empty/null values.
 */
export const DateField = ({ source }: DateFieldProps) => {
  const record = useRecordContext();

  if (!record) return null;

  const dateValue = record[source];
  const formattedDate = formatDate(dateValue);

  return <span>{formattedDate}</span>;
};

DateField.displayName = 'Date';
